import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Card,
  Dialog,
  ErrorState,
  Skeleton,
  SkeletonText,
  Sparkline,
  Tooltip,
  cn,
  formatNumber,
  formatPercent,
  useToast,
} from '@mgdlead/ui';
import { can } from '@mgdlead/shared';
import {
  ArrowRight,
  CircleAlert,
  CircleCheck,
  Copy,
  Link2Off,
  PlugZap,
  RefreshCw,
} from 'lucide-react';

import { useSession } from '../../../auth/useSession';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { workspacePath } from '../../../features/workspace/navigation';
import {
  useAdsConnections,
  useAnalyticsBreakdown,
  useAnalyticsToday,
  useIncidentCounts,
  useMonitors,
  useRecarregarPainel,
  useResyncWhatsAppNow,
  useSyncAdsNow,
  type AnalyticsBreakdown,
  type AnalyticsFunnelRow,
  type AnalyticsOverview,
} from '../../../lib/api';
import type { Periodo } from './analytics-ui';
import { MetaFreshness } from '../../../features/campaigns/CampaignPerformance';
import {
  RecomendacaoCard,
  RecomendacoesErro,
  RecomendacoesSkeleton,
  useRecomendacoes,
} from '../../../features/goals/Recomendacoes';
import { formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';
import { MancheteDoDia } from './HojeManchete';

/* ========================================================================== */
/*  1. Hoje                                                                    */
/* ========================================================================== */

/**
 * O primeiro bloco do painel responde a PRIMEIRA pergunta de quem abre: "quanta gente entrou
 * hoje?" — em frase, não em tile. Fica fora do filtro de período de propósito: o período
 * vale para a análise lá embaixo; "hoje" é sempre hoje.
 */
export function HojeHero() {
  const { workspace } = useWorkspace();
  const session = useSession();
  const today = useAnalyticsToday(workspace.id);
  const nome = session.user?.name?.split(' ')[0];
  const classeHeroi = 'mb-4 border-primary bg-primary p-5 text-primary-foreground md:p-6';

  if (today.isPending) {
    // Skeleton no formato final: a saudação, as 2 linhas da manchete e a faixa de números.
    return (
      <Card padded={false} className={classeHeroi} aria-busy="true">
        <span className="sr-only">Carregando o resumo de hoje</span>
        <Skeleton className="h-3 w-24 bg-primary-foreground/20" />
        <SkeletonText lines={2} className="mt-3 max-w-xl [&>*]:bg-primary-foreground/20" />
        <Skeleton className="mt-5 h-12 w-full bg-primary-foreground/10" />
      </Card>
    );
  }
  if (today.isError || !today.data) {
    return (
      <Card className="mb-4">
        <ErrorState
          title="Não conseguimos montar o resumo de hoje"
          description={today.isError ? humanizeError(today.error) : undefined}
          onRetry={() => void today.refetch()}
        />
      </Card>
    );
  }
  const t = today.data;
  const semMedicaoTotal = t.semMedicao !== null && t.semMedicao.grupos >= t.grupos;

  return (
    <Card padded={false} className={classeHeroi}>
      <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          {nome ? <p className="mb-1 text-sm text-primary-foreground/75">Olá, {nome}</p> : null}
          <MancheteDoDia
            t={t}
            timezone={workspace.timezone}
            monitoresHref={workspacePath(workspace.slug, 'monitoradores')}
          />
          {t.entradasSemHorario > 0 ? (
            /*
              Pedido do dono (06/10): o buraco de quando o número cai não pode sumir com as
              pessoas. Quem a reconexão achou no grupo entra no total, com a frase dizendo
              por que não tem hora — e fica fora do gráfico por hora.
            */
            <p className="mt-2 text-xs text-primary-foreground/75">
              Inclui <span className="nums">{formatNumber(t.entradasSemHorario)}</span>{' '}
              {t.entradasSemHorario === 1 ? 'pessoa que entrou' : 'pessoas que entraram'} enquanto o
              WhatsApp estava desconectado (sem horário exato)
              {t.saidasSemHorario > 0
                ? ` · ${formatNumber(t.saidasSemHorario)} saíram nesse período`
                : ''}
              .
            </p>
          ) : null}
        </div>
        <div className="w-full shrink-0 md:w-60">
          <Sparkline
            tone="brand"
            heightClassName="h-16"
            slots={24}
            label="Entradas de hoje, hora a hora"
            values={t.porHora.slice(0, t.horaAtual + 1)}
          />
          <p
            className="mt-1 flex justify-between text-2xs text-primary-foreground/70"
            aria-hidden="true"
          >
            <span>00h</span>
            <span>agora</span>
            <span>23h</span>
          </p>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-3 border-t border-primary-foreground/20 pt-4 md:grid-cols-4">
        <Numero
          rotulo="Ficaram hoje"
          valor={semMedicaoTotal ? '—' : formatNumber(t.fluxo.ficaram)}
        />
        <Numero
          rotulo="Permanência do dia"
          valor={
            semMedicaoTotal || t.fluxo.taxaFicaram === null
              ? '—'
              : formatPercent(t.fluxo.taxaFicaram, 0)
          }
        />
        <Numero
          rotulo="Pessoas nos grupos"
          valor={t.participantes === null ? '—' : formatNumber(t.participantes)}
        />
        <Numero
          rotulo="Grupos"
          valor={`${formatNumber(t.grupos)}`}
          detalhe={`em ${t.campanhas} ${t.campanhas === 1 ? 'campanha' : 'campanhas'}`}
        />
      </dl>
    </Card>
  );
}

function Numero({ rotulo, valor, detalhe }: { rotulo: string; valor: string; detalhe?: string }) {
  return (
    <div>
      <dt className="text-xs text-primary-foreground/70">{rotulo}</dt>
      <dd className="nums mt-0.5 text-lg font-semibold">
        {valor}
        {detalhe ? (
          <span className="ml-1 text-xs font-normal text-primary-foreground/70">{detalhe}</span>
        ) : null}
      </dd>
    </div>
  );
}

/* ========================================================================== */
/*  2. O que fazer hoje (antes "Precisa de atenção")                           */
/* ========================================================================== */

/**
 * Cada aviso em três camadas (pedido do dono, 06/10: "esses erros não devem aparecer
 * como um monte de texto"):
 *  1. o TÍTULO diz o problema em palavras do cliente;
 *  2. UMA frase diz o que ele causa no bolso;
 *  3. o passo a passo (e o código a copiar) mora atrás de "Como corrigir".
 * Nada de parâmetro técnico solto no cartão.
 */
type Item = {
  chave: string;
  /** Aviso opcional: ganha o "Agora não", que esconde por 30 dias. */
  dispensavel?: boolean;
  icone: typeof CircleAlert;
  titulo: string;
  efeito: string;
  tom: 'danger' | 'warning';
} & ({ tipo: 'link'; href: string; acao: string } | { tipo: 'guia'; guia: Guia });

type Guia = {
  titulo: string;
  porque: string;
  passos: string[];
  codigo?: string;
  depois: { texto: string; href: string };
};

const PARAMETROS_META = 'utm_campaign={{campaign.id}}&utm_term={{adset.id}}&utm_content={{ad.id}}';

export function PrecisaDeAtencao(_props: { periodo: Periodo }) {
  const { workspace } = useWorkspace();
  const counts = useIncidentCounts(workspace.id);
  /*
   * Os avisos olham HOJE, e não o período da tela: corrigido o anúncio, o aviso tem que
   * sumir no dia seguinte — olhando 30 dias, ele ficaria um mês acusando cliques antigos.
   */
  const breakdown = useAnalyticsBreakdown(workspace.id, 'HOJE');
  const [aberto, setAberto] = useState<Guia | null>(null);
  const [dispensados, setDispensados] = useState(() => lerDispensados(workspace.id));
  /*
   * G4: as até 3 recomendações do dia (metas e anúncios), prontas do servidor. Entram depois
   * dos avisos operacionais (incidente, ligação com a Meta), que continuam como eram.
   */
  const recs = useRecomendacoes();
  if (!counts.data || !breakdown.data) return null;

  const itens: Item[] = [];
  if (counts.data.open > 0) {
    itens.push({
      chave: 'incidentes',
      tipo: 'link',
      icone: CircleAlert,
      tom: counts.data.critical > 0 ? 'danger' : 'warning',
      titulo:
        counts.data.open === 1
          ? '1 problema nos seus grupos'
          : `${counts.data.open} problemas nos seus grupos`,
      efeito: 'Enquanto não forem resolvidos, alguns grupos podem estar deixando de receber gente.',
      href: workspacePath(workspace.slug, 'incidentes'),
      acao: 'Ver e resolver',
    });
  }
  const semParametro = breakdown.data.funil.filter(
    (f) => f.metaCampaigns.length > 0 && (f.semCampanhaMeta?.cliques ?? 0) > 0,
  );
  if (semParametro.length > 0) {
    const nomes = semParametro.map((f) => f.name).join(', ');
    itens.push({
      chave: 'utm',
      tipo: 'guia',
      icone: Link2Off,
      tom: 'warning',
      dispensavel: true,
      titulo: 'Anúncios sem identificação',
      efeito:
        'Opcional. O custo por campanha já está certo; com a identificação, você vê também QUAL anúncio trouxe cada pessoa.',
      guia: {
        titulo: 'Identificar de qual anúncio veio cada pessoa',
        porque: `Os cliques de ${nomes} chegam sem a "etiqueta" do anúncio. Com a etiqueta, o MGDLead mostra o custo por pessoa de cada anúncio e conjunto — e você sabe qual pausar e qual escalar.`,
        passos: [
          'No Gerenciador de Anúncios da Meta, abra o anúncio (ou selecione todos e clique em Editar).',
          'Desça até "Rastreamento" e encontre o campo "Parâmetros de URL".',
          'Cole o código abaixo nesse campo e publique.',
        ],
        codigo: PARAMETROS_META,
        depois: {
          texto: 'Vale para os próximos cliques. Confira em Campanhas.',
          href: workspacePath(workspace.slug, 'campanhas'),
        },
      },
    });
  }
  const semMeta = breakdown.data.funil.filter((f) => f.metaCampaigns.length === 0 && f.cliques > 0);
  if (semMeta.length > 0) {
    itens.push({
      chave: 'meta',
      tipo: 'guia',
      icone: PlugZap,
      tom: 'warning',
      titulo: semMeta.length === 1 ? 'Campanha sem custo' : `${semMeta.length} campanhas sem custo`,
      efeito:
        'Recebem cliques, mas sem ligação com a Meta não dá para calcular quanto custou cada pessoa.',
      guia: {
        titulo: 'Ligar a campanha à Meta',
        porque: `${semMeta.map((f) => f.name).join(', ')} ainda não está ligada à campanha da Meta que manda gente para ela. Ligando, aparecem investimento, CPL Meta e CPL real.`,
        passos: [
          'Abra Campanhas.',
          'Na campanha, toque em ⋯ e depois em "Meta Ads".',
          'Escolha a campanha da Meta que anuncia para esses grupos e salve.',
        ],
        depois: { texto: 'Abrir Campanhas', href: workspacePath(workspace.slug, 'campanhas') },
      },
    });
  }

  const visiveis = itens.filter((i) => !dispensados.has(i.chave));
  const dispensar = (chave: string) => {
    gravarDispensado(workspace.id, chave);
    setDispensados(lerDispensados(workspace.id));
  };

  const recomendacoes = recs.estado === 'pronto' ? recs.itens : [];
  if (visiveis.length === 0 && recs.estado === 'pronto' && recomendacoes.length === 0) {
    // Lista vazia é "nada a fazer" (vale como 0) — diferente de erro, que tem o seu cartão.
    return (
      <p className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <CircleCheck aria-hidden="true" className="size-4 text-success" />
        Nada pedindo sua atenção agora.
      </p>
    );
  }

  const classeAcao =
    'mt-3 inline-flex min-h-11 w-full items-center justify-between border-t border-border pt-3 text-sm font-medium text-foreground hover:text-primary-text md:min-h-0';

  return (
    <section aria-label="O que fazer hoje" className="mb-4">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
        O que fazer hoje
        <span className="nums rounded-pill bg-muted px-2 text-xs text-muted-foreground">
          {visiveis.length + recomendacoes.length}
        </span>
      </h2>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {recs.estado === 'carregando' ? <RecomendacoesSkeleton /> : null}
        {recs.estado === 'erro' ? <RecomendacoesErro tentar={recs.tentar} /> : null}
        {visiveis.map((i) => (
          <Card key={i.chave} padded={false} className="flex flex-col p-4">
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  'inline-flex size-9 shrink-0 items-center justify-center rounded-md',
                  i.tom === 'danger'
                    ? 'bg-danger-soft text-danger'
                    : 'bg-warning-soft text-warning',
                )}
              >
                <i.icone aria-hidden="true" className="size-4" />
              </span>
              <div className="min-w-0">
                <p className="font-medium text-foreground">{i.titulo}</p>
                <p className="mt-1 text-sm text-muted-foreground">{i.efeito}</p>
              </div>
            </div>
            {i.tipo === 'link' ? (
              <Link to={i.href} className={classeAcao}>
                {i.acao}
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            ) : (
              <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
                <button
                  type="button"
                  onClick={() => setAberto(i.guia)}
                  className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-foreground hover:text-primary-text md:min-h-0"
                >
                  Como corrigir
                  <ArrowRight aria-hidden="true" className="size-4" />
                </button>
                {i.dispensavel ? (
                  <button
                    type="button"
                    onClick={() => dispensar(i.chave)}
                    className="inline-flex min-h-11 items-center text-xs text-muted-foreground hover:text-foreground md:min-h-0"
                  >
                    Agora não
                  </button>
                ) : null}
              </div>
            )}
          </Card>
        ))}
        {recomendacoes.map((r) => (
          <RecomendacaoCard key={r.chave} item={r} />
        ))}
      </div>
      <GuiaDialog guia={aberto} onClose={() => setAberto(null)} />
    </section>
  );
}

/*
 * "Agora não" lembra por 30 dias NESTE navegador. É conveniência de quem olha (não é estado
 * do negócio), então mora no localStorage — e falhar ao ler/gravar só faz o aviso voltar.
 */
const DISPENSA_MS = 30 * 24 * 60 * 60_000;
const chaveDispensa = (workspaceId: string) => `mgdlead:avisos-dispensados:${workspaceId}`;

function lerDispensados(workspaceId: string): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(chaveDispensa(workspaceId)) ?? '{}') as Record<
      string,
      number
    >;
    const agora = Date.now();
    return new Set(
      Object.entries(raw)
        .filter(([, t]) => agora - t < DISPENSA_MS)
        .map(([k]) => k),
    );
  } catch {
    return new Set();
  }
}

function gravarDispensado(workspaceId: string, chave: string): void {
  try {
    const raw = JSON.parse(localStorage.getItem(chaveDispensa(workspaceId)) ?? '{}') as Record<
      string,
      number
    >;
    raw[chave] = Date.now();
    localStorage.setItem(chaveDispensa(workspaceId), JSON.stringify(raw));
  } catch {
    // sem armazenamento: o aviso só volta a aparecer
  }
}

function GuiaDialog({ guia, onClose }: { guia: Guia | null; onClose: () => void }) {
  const { toast } = useToast();
  const copiar = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast({ title: 'Código copiado', tone: 'success' });
    } catch {
      toast({ title: 'Não deu para copiar — selecione o texto e copie à mão.', tone: 'danger' });
    }
  };
  return (
    <Dialog
      open={guia !== null}
      onOpenChange={(o) => (o ? null : onClose())}
      title={guia?.titulo ?? ''}
      description={guia?.porque}
      size="lg"
      footer={
        guia ? (
          <Link
            to={guia.depois.href}
            onClick={onClose}
            className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary-text md:min-h-0"
          >
            {guia.depois.texto}
            <ArrowRight aria-hidden="true" className="size-4" />
          </Link>
        ) : null
      }
    >
      {guia ? (
        <div className="flex flex-col gap-4">
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-foreground">
            {guia.passos.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ol>
          {guia.codigo ? (
            <div className="flex flex-col gap-2">
              <code className="block break-all rounded-md border border-border bg-muted p-3 text-xs text-foreground">
                {guia.codigo}
              </code>
              <Button
                variant="primary"
                block
                leadingIcon={<Copy />}
                onClick={() => void copiar(guia.codigo ?? '')}
              >
                Copiar código
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

/* ========================================================================== */
/*  3. Funil do período + por campanha                                          */
/* ========================================================================== */

/**
 * Do clique ao "ficou", em FUNIL (pedido do dono, 10/10): uma coluna por degrau, com o número
 * grande, quanto passou do degrau de cima e um bloco cuja altura é a fração do primeiro. Entre
 * os blocos, a faixa que afina mostra onde a gente fica pelo caminho. Tocar num degrau abre a
 * explicação embaixo; o degrau "Entradas" carrega a taxa de entrada que vem pronta do servidor.
 */
type Degrau = {
  chave: string;
  rotulo: string;
  valor: number;
  /** "82% dos únicos" — pronto, ou a fração do degrau de cima. */
  pct: number | null;
  base: string | null;
  ajuda: string;
  /** Quantos ficaram pelo caminho desde o degrau de cima; `null` quando não é a mesma gente. */
  perdidos: number | null;
  verbo: string;
};

export function FunilDoPeriodo({
  overview,
  funil,
  pagina,
}: {
  overview: AnalyticsOverview | undefined;
  funil: AnalyticsFunnelRow[] | undefined;
  /** A página de ofertas (visitas da Meta), pronta do servidor; `null` = a Meta não contou. */
  pagina?: AnalyticsBreakdown['pagina'];
}) {
  const [escolhido, setEscolhido] = useState('entradas');
  if (!overview || !funil) return null;
  const cliques = overview.attribution.clicks;
  if (cliques === 0) return null;
  const unicos = overview.attribution.uniqueVisitors;
  const entradas = funil.reduce((t, f) => t + f.entradas, 0);
  const ficaram = funil.reduce((t, f) => t + f.entradas - f.evasao.sairamNoMesmoDia, 0);
  const fracao = (v: number, de: number) => (de > 0 ? v / de : null);

  const degraus: Degrau[] = [
    ...(pagina
      ? [
          {
            chave: 'visitas',
            rotulo: 'Visitas na página',
            valor: pagina.visitas,
            pct: null,
            base: null,
            ajuda:
              'Quantas vezes a página de ofertas abriu, contadas pela Meta (só campanhas vinculadas a um bloco).',
            perdidos: null,
            verbo: '',
          },
        ]
      : []),
    {
      chave: 'cliques',
      rotulo: 'Cliques',
      valor: cliques,
      // A taxa de clique da página só conta os blocos que TÊM visita da Meta (`paginaDeOfertas`).
      pct: pagina ? pagina.taxaDeClique : null,
      base: pagina ? 'das visitas' : null,
      ajuda:
        'Todos os cliques no link que leva para o grupo, inclusive a mesma pessoa clicando de novo.',
      perdidos: null,
      verbo: '',
    },
    {
      chave: 'unicos',
      rotulo: 'Cliques únicos',
      valor: unicos,
      pct: fracao(unicos, cliques),
      base: 'dos cliques',
      ajuda:
        'Pessoas diferentes que clicaram: o mesmo celular clicando várias vezes conta uma vez só.',
      perdidos: null,
      verbo: '',
    },
    {
      chave: 'entradas',
      rotulo: 'Entradas',
      valor: entradas,
      pct: overview.attribution.entryRate,
      base: 'dos únicos',
      ajuda:
        'Quem clicou e entrou no grupo. É a sua taxa de entrada: entradas ÷ pessoas que clicaram. O WhatsApp não avisa quem veio do link, então o número é uma estimativa por baixo.',
      perdidos: Math.max(0, unicos - entradas),
      verbo: 'clicaram e não entraram',
    },
    {
      chave: 'ficaram',
      rotulo: 'Ficaram',
      valor: ficaram,
      pct: fracao(ficaram, entradas),
      base: 'das entradas',
      ajuda: 'Entraram e não saíram no mesmo dia.',
      perdidos: Math.max(0, entradas - ficaram),
      verbo: 'saíram no mesmo dia',
    },
  ];
  const topo = Math.max(1, ...degraus.map((d) => d.valor));
  const sel = degraus.find((d) => d.chave === escolhido) ?? degraus[degraus.length - 2]!;
  const porCampanha = [...funil]
    .filter((f) => f.entradas > 0)
    .sort((a, b) => b.entradas - a.entradas);
  const maxCamp = Math.max(1, ...porCampanha.map((f) => f.entradas));

  // Desenho: cada degrau ocupa 100 unidades de largura; o bloco, as primeiras 62.
  const n = degraus.length;
  const BLOCO = 62;
  const alt = (v: number) => Math.max(3, (v / topo) * 96);
  const topoDe = (v: number) => 50 - alt(v) / 2;
  const tom = (i: number) => 0.4 + (0.6 * i) / Math.max(1, n - 1);

  return (
    <div className="mt-4 flex flex-col gap-4">
      <Card padded={false} className="p-4 md:p-5">
        <h2 className="text-sm font-semibold text-foreground">Do clique ao grupo</h2>
        <p className="text-xs text-muted-foreground">
          Onde as pessoas ficam pelo caminho no período. Toque num degrau para entender.
        </p>

        {/* Celular: o funil em pé, um degrau por linha. */}
        <ol className="mt-4 flex flex-col gap-1.5 md:hidden">
          {degraus.map((d, i) => (
            <li key={d.chave}>
              <button
                type="button"
                onClick={() => setEscolhido(d.chave)}
                aria-pressed={sel.chave === d.chave}
                className={cn(
                  'w-full rounded-lg px-2 py-2 text-left transition-colors',
                  sel.chave === d.chave ? 'bg-muted' : 'hover:bg-muted/60',
                )}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-muted-foreground">{d.rotulo}</span>
                  <span className="nums">
                    <strong className="text-lg text-foreground">{formatNumber(d.valor)}</strong>
                    {d.pct !== null ? (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {formatPercent(d.pct, 0)} {d.base}
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="mt-1.5 flex justify-center">
                  <span
                    className="block h-3 rounded-pill bg-primary"
                    style={{
                      width: `${Math.max(2, (d.valor / topo) * 100)}%`,
                      opacity: tom(i),
                    }}
                  />
                </div>
              </button>
            </li>
          ))}
        </ol>

        {/* Tela larga: o funil deitado, colunas com a faixa afinando entre elas. */}
        <div className="relative mt-4 hidden md:block">
          <div className="grid" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
            {degraus.map((d) => (
              <button
                key={d.chave}
                type="button"
                onClick={() => setEscolhido(d.chave)}
                aria-pressed={sel.chave === d.chave}
                className={cn(
                  'flex min-h-80 flex-col rounded-xl px-3 pt-3 text-left transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  sel.chave === d.chave ? 'bg-muted' : 'hover:bg-muted/50',
                )}
              >
                <span className="text-sm text-muted-foreground">{d.rotulo}</span>
                <strong className="nums mt-1 text-2xl font-semibold text-foreground lg:text-3xl">
                  {formatNumber(d.valor)}
                </strong>
                <span className="nums text-xs text-muted-foreground">
                  {d.pct !== null ? `${formatPercent(d.pct, 0)} ${d.base}` : 'o começo do caminho'}
                </span>
              </button>
            ))}
          </div>
          <svg
            aria-hidden="true"
            viewBox={`0 0 ${n * 100} 100`}
            preserveAspectRatio="none"
            className="pointer-events-none absolute inset-x-0 bottom-4 h-40 w-full"
          >
            {degraus.map((d, i) => {
              const prox = degraus[i + 1];
              const x = i * 100 + 6;
              return (
                <g key={d.chave}>
                  {prox ? (
                    <polygon
                      points={[
                        `${x + BLOCO},${topoDe(d.valor)}`,
                        `${x + 100},${topoDe(prox.valor)}`,
                        `${x + 100},${topoDe(prox.valor) + alt(prox.valor)}`,
                        `${x + BLOCO},${topoDe(d.valor) + alt(d.valor)}`,
                      ].join(' ')}
                      fill="var(--color-primary)"
                      opacity={0.14}
                    />
                  ) : null}
                  <rect
                    x={x}
                    y={topoDe(d.valor)}
                    width={BLOCO}
                    height={alt(d.valor)}
                    fill="var(--color-primary)"
                    opacity={tom(i)}
                  />
                </g>
              );
            })}
          </svg>
        </div>

        <div className="mt-4 rounded-xl border border-border p-4" aria-live="polite">
          <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-base font-semibold text-foreground">{sel.rotulo}</span>
            <span className="nums text-xl font-semibold text-foreground">
              {formatNumber(sel.valor)}
            </span>
            {sel.pct !== null ? (
              <span className="nums text-sm text-muted-foreground">
                {formatPercent(sel.pct, 0)} {sel.base}
              </span>
            ) : null}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">{sel.ajuda}</p>
          {sel.perdidos !== null && sel.perdidos > 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Ficaram pelo caminho:{' '}
              <strong className="nums text-foreground">{formatNumber(sel.perdidos)}</strong>{' '}
              {sel.verbo}.
            </p>
          ) : null}
        </div>
      </Card>

      <Card padded={false} className="p-4">
        <h2 className="text-sm font-semibold text-foreground">Entradas por campanha</h2>
        <p className="text-xs text-muted-foreground">
          Quem trouxe gente no período, e quantos ficaram.
        </p>
        {porCampanha.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            Nenhuma entrada ligada a campanha no período.
          </p>
        ) : (
          <ul className="mt-4 flex flex-col gap-3">
            {porCampanha.map((f) => (
              <li key={f.campaignId}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate text-foreground">{f.name}</span>
                  <span className="nums shrink-0 text-muted-foreground">
                    <strong className="text-foreground">{formatNumber(f.entradas)}</strong> ·{' '}
                    {formatNumber(f.entradas - f.evasao.sairamNoMesmoDia)} ficaram
                  </span>
                </div>
                <div className="mt-1 h-2.5 rounded-pill bg-muted">
                  <div
                    className="h-full rounded-pill bg-primary"
                    style={{ width: `${(f.entradas / maxCamp) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* ========================================================================== */
/*  0. De quando são estes números                                             */
/* ========================================================================== */

/**
 * Uma linha discreta no topo: de quando é o dado do WhatsApp e o da Meta, cada um com um
 * ↻ pequeno (pedido do dono, 06/10: "bem discreto mesmo"). Os dois se atualizam sozinhos —
 * o botão é para quem não quer esperar a próxima leitura.
 */
export function AtualizacaoDosDados() {
  const { workspace, role } = useWorkspace();
  const monitores = useMonitors(workspace.id).data?.monitors;
  const podeMeta = can(role, 'integration:read');
  const conexoesQuery = useAdsConnections(workspace.id, podeMeta);
  const conexoes = conexoesQuery.data;
  const refetchConexoes = conexoesQuery.refetch;
  const syncMeta = useSyncAdsNow();
  const resync = useResyncWhatsAppNow();
  const recarregar = useRecarregarPainel();
  const { toast } = useToast();

  const vivos = (monitores ?? []).filter((m) => m.archivedAt === null);
  const caido = vivos.some(
    (m) => m.lastConnectedAt !== null && m.groupsCount > 0 && m.status !== 'CONNECTED',
  );
  const ultimoEvento = vivos
    .map((m) => m.lastEventAt)
    .filter((d): d is NonNullable<typeof d> => d !== null)
    .map((d) => new Date(d).toISOString())
    .sort()
    .at(-1);
  const contasAtivas = (conexoes ?? []).filter((c) => c.status !== 'DISABLED');

  /*
   * O ↻ só termina quando o dado novo CHEGA: a Meta é lida pelo worker (fila), então o botão
   * fica girando e a lista de contas é reconsultada a cada 5 s até a `lastSyncAt` mudar —
   * aí a tela inteira recarrega. Teto de 3 min: depois disso, o ciclo normal assume.
   */
  const [esperandoMeta, setEsperandoMeta] = useState<{ antes: string; ate: number } | null>(null);
  const ultimaLeituraMeta =
    contasAtivas
      .map((c) => c.lastSyncAt ?? '')
      .sort()
      .at(-1) ?? '';
  useEffect(() => {
    if (!esperandoMeta) return;
    if (ultimaLeituraMeta !== esperandoMeta.antes) {
      setEsperandoMeta(null);
      recarregar();
      toast({
        title: 'Meta atualizada',
        description: 'Os números da tela já são os novos.',
        tone: 'success',
      });
      return;
    }
    if (Date.now() > esperandoMeta.ate) {
      setEsperandoMeta(null);
      recarregar();
      return;
    }
    const t = setTimeout(() => void refetchConexoes(), 5_000);
    return () => clearTimeout(t);
  });

  /*
   * O WhatsApp é relido em segundo plano, um grupo por vez (2 s entre eles): a tela
   * recarrega a cada 10 s enquanto isso, por até 2 min — os números vão aparecendo.
   */
  const [esperandoWhatsAte, setEsperandoWhatsAte] = useState<number | null>(null);
  const [, setTique] = useState(0);
  useEffect(() => {
    if (esperandoWhatsAte === null) return;
    const t = setTimeout(() => {
      recarregar();
      if (Date.now() > esperandoWhatsAte) {
        setEsperandoWhatsAte(null);
        toast({ title: 'Grupos atualizados', tone: 'success' });
      } else {
        setTique((n) => n + 1);
      }
    }, 10_000);
    return () => clearTimeout(t);
  });

  const atualizarMeta = async () => {
    const r = await Promise.allSettled(
      contasAtivas.map((c) =>
        syncMeta.mutateAsync({ workspaceId: workspace.id, connectionId: c.id }),
      ),
    );
    const pedidas = r.filter((x) => x.status === 'fulfilled' && x.value.queued).length;
    if (pedidas > 0) {
      setEsperandoMeta({ antes: ultimaLeituraMeta, ate: Date.now() + 3 * 60_000 });
    } else {
      recarregar();
      toast({
        title: 'A Meta foi lida há pouco',
        description:
          'A tela foi recarregada com a última leitura. Dá para pedir outra em alguns minutos.',
      });
    }
  };
  const atualizarWhats = () =>
    resync.mutate(
      { workspaceId: workspace.id },
      {
        onSuccess: (r) => {
          if (r.started > 0) {
            setEsperandoWhatsAte(Date.now() + 2 * 60_000);
            return;
          }
          recarregar();
          toast(
            r.retryInSeconds > 0
              ? {
                  title: 'Os grupos foram atualizados há pouco',
                  description: `A tela foi recarregada. Dá para reler os grupos de novo em ${Math.ceil(r.retryInSeconds / 60)} min — mais rápido que isso o WhatsApp bloqueia.`,
                }
              : { title: 'Nenhum número conectado para atualizar' },
          );
        },
      },
    );

  if (vivos.length === 0 && contasAtivas.length === 0) return null;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {vivos.length > 0 ? (
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn('size-1.5 rounded-pill', caido ? 'bg-danger' : 'bg-success')}
          />
          {caido
            ? 'WhatsApp: número fora do ar'
            : `WhatsApp ao vivo${ultimoEvento ? ` · último evento ${formatRelative(ultimoEvento) ?? ''}` : ''}`}
          {can(role, 'group:import') ? (
            <BotaoAtualizar
              rotulo="Atualizar os grupos do WhatsApp agora"
              ocupado={resync.isPending || esperandoWhatsAte !== null}
              onClick={atualizarWhats}
            />
          ) : null}
        </span>
      ) : null}
      {contasAtivas.length > 0 ? (
        <span className="inline-flex items-center gap-1.5">
          <MetaFreshness />
          <BotaoAtualizar
            rotulo="Ler a Meta agora"
            ocupado={syncMeta.isPending || esperandoMeta !== null}
            onClick={() => void atualizarMeta()}
          />
        </span>
      ) : null}
    </div>
  );
}

function BotaoAtualizar({
  rotulo,
  ocupado,
  onClick,
}: {
  rotulo: string;
  ocupado: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip content={rotulo}>
      <button
        type="button"
        aria-label={rotulo}
        disabled={ocupado}
        onClick={onClick}
        className="inline-flex size-7 items-center justify-center rounded-pill text-subtle-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      >
        <RefreshCw aria-hidden="true" className={cn('size-3.5', ocupado && 'animate-spin')} />
      </button>
    </Tooltip>
  );
}
